/**
 * Category Extractor
 *
 * Splits the raw Journal API response into typed, categorized arrays.
 * This is a structural extraction layer that ensures the response
 * is properly typed for downstream pipeline stages.
 *
 * Also transforms raw API fields to match the expected typed interfaces:
 * - Input records: maps `input` array → `food_items` string, builds `creator_name`
 * - Output records: maps `output` field → `category`, builds `creator_name`
 * - Note records: maps `notes` → `note`, `image_url_s3` → `image_url`, builds `creator_name`
 */

import type {
  RawJournalResponse,
  RawInputRecord,
  RawOutputRecord,
  RawNoteRecord,
  RawVitalRecord,
  RawAdlRecord,
} from '../types';

/** Categorized journal data with vitals split by type */
export interface CategorizedJournalData {
  input: RawInputRecord[];
  output: RawOutputRecord[];
  notes: RawNoteRecord[];
  vitals: {
    heartrate: RawVitalRecord[];
    bloodpressure: RawVitalRecord[];
    temperature: RawVitalRecord[];
    spo2: RawVitalRecord[];
  };
  adl: RawAdlRecord[];
}

/**
 * Build a creator_name string from the nested creator object.
 * Falls back to empty string if creator is missing.
 */
function buildCreatorName(record: any): string {
  if (record.creator_name) return record.creator_name;
  if (record.creator) {
    const first = record.creator.first_name || '';
    const last = record.creator.last_name || '';
    return `${first} ${last}`.trim();
  }
  return '';
}

/**
 * Convert customer_id to string regardless of whether it arrives as number or string.
 */
function toStringId(value: any): string {
  if (value == null) return '';
  return String(value);
}

/**
 * Transform a raw API input record to match the RawInputRecord interface.
 *
 * The production API returns:
 * - `input`: array of {food, volumne, unit} objects (NOT an encoded string called `food_items`)
 * - `creator`: {first_name, last_name} (NOT `creator_name`)
 * - `customer_id`: number (NOT string)
 */
function transformInputRecord(raw: any): RawInputRecord {
  // Convert the `input` array to an encoded food_items string
  // Format: {name};{amount};{unit},{name};{amount};{unit},...
  let foodItems = '';
  if (Array.isArray(raw.input)) {
    foodItems = raw.input
      .map((item: any) => {
        const name = (item.food || '').replace(/,/g, '[U002C]').replace(/;/g, '[U003B]');
        const amount = item.volumne || item.volume || '0';
        const unit = item.unit || 'unit';
        return `${name};${amount};${unit}`;
      })
      .join(',');
  } else if (typeof raw.food_items === 'string') {
    // Already in the expected format (e.g., from tests)
    foodItems = raw.food_items;
  }

  return {
    id: raw.id,
    customer_id: toStringId(raw.customer_id),
    recorded_at: raw.recorded_at || '',
    created_at: raw.created_at || '',
    created_by: toStringId(raw.created_by),
    creator_name: buildCreatorName(raw),
    type: raw.type ?? 1,
    meal: raw.meal ?? 1,
    food_items: foodItems,
    notes: raw.notes ?? '',
  };
}

/**
 * Transform a raw API output record to match the RawOutputRecord interface.
 *
 * The production API returns:
 * - `output` field (NOT `category`) for the category code
 * - `creator`: {first_name, last_name} (NOT `creator_name`)
 * - `customer_id`: number (NOT string)
 */
function transformOutputRecord(raw: any): RawOutputRecord {
  return {
    id: raw.id,
    customer_id: toStringId(raw.customer_id),
    recorded_at: raw.recorded_at || '',
    created_at: raw.created_at || '',
    created_by: toStringId(raw.created_by),
    creator_name: buildCreatorName(raw),
    category: raw.category ?? raw.output ?? 1,
    type: raw.type ?? 1,
    amount: raw.amount ?? 1,
    color: raw.color ?? 1,
    pattern: raw.pattern ?? 0,
    notes: raw.notes ?? '',
  };
}

/**
 * Transform a raw API note record to match the RawNoteRecord interface.
 *
 * The production API returns:
 * - `notes` field (NOT `note`) for the note text
 * - `image_url_s3` (NOT `image_url`) for the image URL
 * - `creator`: {first_name, last_name} (NOT `creator_name`)
 * - `customer_id`: number (NOT string)
 */
function transformNoteRecord(raw: any): RawNoteRecord {
  return {
    id: raw.id,
    customer_id: toStringId(raw.customer_id),
    recorded_at: raw.recorded_at || '',
    created_at: raw.created_at || '',
    created_by: toStringId(raw.created_by),
    creator_name: buildCreatorName(raw),
    note: raw.note ?? raw.notes ?? '',
    image_url: raw.image_url ?? raw.image_url_s3 ?? null,
    image_id: raw.image_id ?? null,
  };
}

/**
 * Transform a raw API vital record to match the RawVitalRecord interface.
 */
function transformVitalRecord(raw: any): RawVitalRecord {
  return {
    id: raw.id,
    customer_id: toStringId(raw.customer_id),
    recorded_at: raw.recorded_at || '',
    created_at: raw.created_at || '',
    created_by: toStringId(raw.created_by),
    creator_name: buildCreatorName(raw),
    value: raw.value ?? 0,
    value2: raw.value2,
    notes: raw.notes ?? '',
  };
}

/**
 * Transform a raw API ADL record to match the RawAdlRecord interface.
 */
function transformAdlRecord(raw: any): RawAdlRecord {
  return {
    id: raw.id,
    customer_id: toStringId(raw.customer_id),
    recorded_at: raw.recorded_at || '',
    created_at: raw.created_at || '',
    created_by: toStringId(raw.created_by),
    creator_name: buildCreatorName(raw),
    activity: raw.activity ?? '',
    notes: raw.notes ?? '',
  };
}

/**
 * Extracts and categorizes arrays from the raw Journal API response.
 *
 * Transforms each record to match the expected typed interfaces, handling
 * field name mismatches between the production API and the internal types.
 * Missing arrays in the response are treated as empty arrays.
 */
export function extractCategories(response: RawJournalResponse): CategorizedJournalData {
  const inputRaw = response.input ?? [];
  const outputRaw = response.output ?? [];
  const noteRaw = response.note ?? [];
  const heartrateRaw = response.heartrate ?? [];
  const bloodpressureRaw = response.bloodpressure ?? [];
  const temperatureRaw = response.temperature ?? [];
  const spo2Raw = response.spo2 ?? [];
  const adlRaw = response.adl ?? [];

  return {
    input: inputRaw.map(transformInputRecord),
    output: outputRaw.map(transformOutputRecord),
    notes: noteRaw.map(transformNoteRecord),
    vitals: {
      heartrate: heartrateRaw.map(transformVitalRecord),
      bloodpressure: bloodpressureRaw.map(transformVitalRecord),
      temperature: temperatureRaw.map(transformVitalRecord),
      spo2: spo2Raw.map(transformVitalRecord),
    },
    adl: adlRaw.map(transformAdlRecord),
  };
}
